import type AbilitiesService from 'ember-can/services/abilities';
import type VisitorProfileModel from 'garaje/models/visitor-profile';
import type FeatureFlagsService from 'garaje/services/feature-flags';

/**
 * Single source of truth for whether `<Visitors::Profiles::HoverCard>` should
 * mount on a row. Used by `invites/table/invite-list/name-cell.gts` and
 * `pods/components/entries/table/entry-feed-item/component.ts` so the gate is
 * defined once.
 *
 * Four conditions, all required:
 *  - The row's source record has a sideloaded visitor profile to display.
 *  - The row has a defined source record id (forwarded as `@id` to the hover
 *    card's CTAs — undefined ids would produce broken `<LinkTo @model>`).
 *  - The `unified_visitor_profile_p1` feature flag is on for this user.
 *  - The user has `read visitor-profile`. The hover card surfaces email /
 *    phone / visit stats / internal notes — all visitor-profile-scoped data
 *    that other entry points (visitors/entry/template.hbs:31,
 *    visitors/invites/show/template.hbs:35) gate on the same ability.
 */
export const VISITOR_PROFILE_HOVER_CARD_FLAG = 'unified_visitor_profile_p1';
export const VISITOR_PROFILE_HOVER_CARD_ABILITY = 'read visitor-profile';

export function shouldShowVisitorProfileHoverCard(args: {
  visitorProfile: VisitorProfileModel | null | undefined;
  recordId: string | null | undefined;
  featureFlags: FeatureFlagsService;
  abilities: AbilitiesService;
}): boolean {
  return (
    Boolean(args.visitorProfile) &&
    Boolean(args.recordId) &&
    args.featureFlags.isEnabled(VISITOR_PROFILE_HOVER_CARD_FLAG) &&
    args.abilities.can(VISITOR_PROFILE_HOVER_CARD_ABILITY)
  );
}
